import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';

const CollageSkeletonShell = styled(SkeletonPageShell).attrs({ $padding: '20px 0 92px' })``;

const CollageSkeletonHeader = styled(SkeletonPageHeader)``;

const CollageTabsSkeleton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 18px;
    min-width: 0;
`;

const CollageTableSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
    min-width: 0;
`;

const CollageTableRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1fr) 180px;
    gap: 18px;
    align-items: center;
    min-height: 58px;
    padding: 14px 18px;
    border-bottom: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }

    @media (max-width: 700px) {
        grid-template-columns: minmax(0, 1fr) 72px;
    }
`;

const CollageSectionSkeleton = styled(SkeletonSurface).attrs({ $padding: '24px' })``;

const CollageGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    min-width: 0;

    @media (max-width: 1000px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const CollageFooterSkeleton = styled(SkeletonFooterBar)``;

export const CollageLayoutPageSkeleton: FC = () => (
    <CollageSkeletonShell aria-hidden='true'>
        <CollageSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(260px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='86px' $height='36px' $radius='6px' />
        </CollageSkeletonHeader>
        <CollageTabsSkeleton>
            <SkeletonBlock $width='104px' $height='34px' $radius='17px' />
            <SkeletonBlock $width='170px' $height='34px' $radius='17px' />
        </CollageTabsSkeleton>
        <CollageTableSkeleton>
            {Array.from({ length: 7 }).map((_, index) => (
                <CollageTableRowSkeleton key={index}>
                    <SkeletonRow $gap='10px'>
                        <SkeletonBlock $width={index % 2 === 0 ? '220px' : '160px'} $height='15px' />
                        <SkeletonBlock $width='84px' $height='22px' $radius='11px' />
                    </SkeletonRow>
                    <SkeletonRow $gap='8px' $justify='flex-end'>
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    </SkeletonRow>
                </CollageTableRowSkeleton>
            ))}
        </CollageTableSkeleton>
    </CollageSkeletonShell>
);

export const CollageLayoutRulePageSkeleton: FC = () => (
    <CollageSkeletonShell aria-hidden='true'>
        <CollageSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(260px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
            </SkeletonStack>
        </CollageSkeletonHeader>
        <SkeletonStack $gap='18px'>
            <CollageSectionSkeleton>
                <SkeletonStack $gap='14px'>
                    <SkeletonBlock $width='86px' $height='20px' />
                    <SkeletonBlock $width='78px' $height='13px' />
                    <SkeletonBlock $height='38px' $radius='6px' />
                </SkeletonStack>
            </CollageSectionSkeleton>
            <CollageSectionSkeleton>
                <SkeletonStack $gap='14px'>
                    <SkeletonBlock $width='150px' $height='20px' />
                    <SkeletonBlock $width='min(560px, 100%)' $height='13px' />
                    <CollageGridSkeleton>
                        {Array.from({ length: 8 }).map((_, index) => (
                            <SkeletonStack key={index} $gap='8px' $align='center'>
                                <SkeletonBlock $width='90px' $height='90px' $radius='8px' />
                                <SkeletonBlock $width='74px' $height='12px' />
                            </SkeletonStack>
                        ))}
                    </CollageGridSkeleton>
                </SkeletonStack>
            </CollageSectionSkeleton>
        </SkeletonStack>
        <CollageFooterSkeleton>
            <SkeletonBlock $width='110px' $height='36px' $radius='6px' />
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
        </CollageFooterSkeleton>
    </CollageSkeletonShell>
);
